// IMPORT React
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Dialog,
  AppBar,
  Toolbar,
  Divider,
  DialogContent,
  colors,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import { CustomTextField } from "../../../components/CustomFields/TextField";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import { PreferencesContext } from "../../../PreferenceContext";
import {
  CreateVaccine,
  GetVaccineDetail,
  GetVaccineList,
  UpdateVaccine,
} from "../../../services/VaccineService";
import {
  CustomAsyncSelect,
  CustomSelect,
} from "../../../components/CustomFields/CustomSelect";
import { PAGE_SIZE } from "../../../services/common/const";
import DatePickerComponent from "../../../components/CustomFields/DatePicker";
import { handleFocus } from "../../../shared/CommonFunction";
import AddIcon from "@mui/icons-material/Add";
import { ClearIcon } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { format, isValid } from "date-fns";
import VaccineSave from "../../Vaccines/VaccineSave";
import VaccineGroupSave from "../../Vaccines/VaccineGroupSave";
import { selectOption } from "../../../services/models/Context";
import { GetVaccineGroupList } from "../../../services/VaccineGroupService";

// SCHEMA FOR FORM
interface formInitialValueTypes {
  vaccineId: selectOption | null;
  vaccineGenericName: string;
  standardDurationBetweenPreviousCurrentDoseValue: number;
  standardDurationBetweenPreviousCurrentDoseMode: string;
  consumerId: number;
  orgId: number;
  branchId: number;
  templateName: string;
  vaccineDoseColl: Array<IFormDetail>;
  isDeleted: boolean;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
interface Parameters {
  vaccineId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
}
const parameters: Parameters = {
  vaccineId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
};

// SCHEMA FOR DETAIL COLL OF FORM
interface IFormDetail {
  vaccineTemplateDoseId: number;
  displayOrder: number;
  isFirstDose: boolean;
  doseOder: number;
  doseName: string;
  firstDoseRefCode: string;
  firstDoseRefName: string;
  firstDoseRefDate: Date | null;
  firstDoseAgeValue: number;
  firstDoseAgeMode: string | null;
  firstDoseRefVaccineDoseId: number;
  durationBetweenPreviousCurrentDoseValue: number;
  durationBetweenPreviousCurrentDoseMode: string | null;
  maxCatchUpAgeValue: number;
  maxCatchUpAgeMode: string | null;
  isDeleted: boolean;
  mode: string;
  vaccineGroupId: any;
  vaccineGroupName: string;
}

//DEFAULT SCHEMA VALUE FOR FORM DETAIL
const initVaccineDoseDetail: IFormDetail = {
  vaccineTemplateDoseId: 0,
  displayOrder: 0,
  isFirstDose: true,
  doseOder: 0,
  doseName: "",
  firstDoseRefCode: "",
  firstDoseRefName: "",
  firstDoseRefDate: null,
  firstDoseAgeValue: 0,
  firstDoseAgeMode: null,
  firstDoseRefVaccineDoseId: 0,
  durationBetweenPreviousCurrentDoseValue: 0,
  durationBetweenPreviousCurrentDoseMode: null,
  maxCatchUpAgeValue: 0,
  maxCatchUpAgeMode: null,
  isDeleted: false,
  mode: "I",
  vaccineGroupId: null,
  vaccineGroupName: "",
};

const firstDoseRefOptions = [
  { value: "AGE_BASED", label: "Age (To be count from Birth Date)" },
  { value: "TODAY", label: "Today" },
  { value: "LMP", label: "LMP" },
  { value: "CUSTOM", label: "Custom" },
  { value: "DEPENDENT", label: "Dependent on other vaccine" },
];

const ageOptions = [
  { value: "DAY", label: "Day" },
  { value: "WEEK", label: "Week" },
  { value: "MONTH", label: "Month" },
  { value: "YEAR", label: "Year" },
];

interface SplitButtonProps {
  options: { label: string; value: "first" | "follow" }[];
  value?: "first" | "follow";
  defaultValue?: "first" | "follow";
  onChange?: (value: "first" | "follow") => void;
}

const SplitButton: React.FC<SplitButtonProps> = ({
  options,
  value,
  defaultValue = "first",
  onChange,
}) => {
  const [selected, setSelected] = useState<"first" | "follow">(defaultValue);

  useEffect(() => {
    if (value) setSelected(value);
  }, [value]);

  const handleClick = (val: "first" | "follow") => {
    setSelected(val);
    onChange?.(val);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        border: "2px solid #285cdb",
        // borderRadius: "9999px",
        borderRadius: "4px",
        width: "140px",
        overflow: "hidden",
        cursor: "pointer",
        position: "relative",
        transition: "0.3s",
        userSelect: "none",
        zIndex: 0,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: selected === options[0].value ? 0 : "50%",
          width: "50%",
          height: "100%",
          backgroundColor: "#285cdb",
          transition: "left 0.3s ease",
          textSize: "9px",
          zIndex: 0,
        }}
      />
      {options.map((opt) => (
        <Box
          key={opt.value}
          onClick={() => handleClick(opt.value)}
          sx={{
            flex: 1,
            color: selected === opt.value ? "#fff" : "#808080",
            fontWeight: 300,
            padding: "2px",
            fontSize: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            textSize: "10px",
            transition: "color 0.3s ease",
          }}
        >
          {opt.label}
        </Box>
      ))}
    </Box>
  );
};

export default function VaccineTemplateDoseSave(Props: any) {
  const { isForPopUp = false, handleClosePopUp, dose, dependentOption } = Props;
  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const theme = useTheme();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // BREAK POINTS
  // const mdd = useMediaQuery(theme.breakpoints.down("md"));
  const xld = useMediaQuery(theme.breakpoints.down("xl"));
  // STATES FOR DATA STORAGE  -- PLEASE USE PROPER TYPES FOR STATE
  const [vaccineData, setVaccineData] = useState<any[]>([]);

  // DEFAULT SCHEMA VALUE FOR FORM
  const initVaccineDoseData: formInitialValueTypes = {
    vaccineId: null,
    vaccineGenericName: "",
    standardDurationBetweenPreviousCurrentDoseValue: 0,
    standardDurationBetweenPreviousCurrentDoseMode: "",
    consumerId: 0,
    branchId: 0,
    orgId: 0,
    templateName: "",
    vaccineDoseColl: [initVaccineDoseDetail],
    isDeleted: false,
  };

  // GETTING PARAMS
  let urlParams = useQuery();
  let curRawVaccineId = urlParams.get("q");
  let vaccineTemplateId = 0;
  if (curRawVaccineId !== null && curRawVaccineId !== undefined) {
    let decId = edService.decrypt(curRawVaccineId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      vaccineTemplateId = +decId;
    }
  } else {
    // if (urlParams.size > 0) {
    //     navigate("/not-found");
    // }
  }

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
  const [vaccineDoseData, setVaccineDoseData] =
    useState<formInitialValueTypes>(initVaccineDoseData);
  const [vaccineGroupOptions, setVaccineGroupOptions] = useState<
    selectOption[]
  >([]);

  const getVaccineGroupList = async (searchValue: string, oninit: boolean) => {
    try {
      let vaccineGroups = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", parameters.pageIndex?.toString());
      params.append("pageSize", parameters.pageSize?.toString());
      params.append("sortColumn", parameters.sortColumn);
      params.append("sortDirection", parameters.sortDirection);
      params.append("searchValue", searchValue);
      const result: any = await GetVaccineGroupList(params);

      if (result?.data?.data?.vaccineGroupColl) {
        const vaccineGroups = result?.data?.data?.vaccineGroupColl?.map(
          (item: any) => ({
            label: item.name,
            value: item.vaccineGroupId?.toString(),
          }),
        );
        if (oninit) {
          setVaccineGroupOptions(vaccineGroups);
        }
        return vaccineGroups;
      } else {
      }
      setIsLoading(false);
    } catch (e) {
      console.log("err:", e);
    }
  };

  // FILTER  VACCINE
  async function filterVaccineGroup(inputValue: string) {
    let result = await getVaccineGroupList(inputValue, false);
    return result;
  }

  //  VACCINE LOAD ON SEARCH
  const loadVaccineGroupList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterVaccineGroup(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
    rowIndex: undefined as number | undefined,
  });

  const handleClickOpen = (
    value: boolean,
    label: string,
    code: string,
    rowIndex?: number,
  ) => {
    setCustomPopUp({
      ...customPopUp,
      open: value,
      name: label,
      code: code,
      rowIndex,
    });
  };

  const handleClose = () => {
    setCustomPopUp({ ...customPopUp, open: false });
  };

  const handleManagePopup = async (
    code: any,
    data?: any,
    rowIndex?: number,
  ) => {
    setIsLoading(true);
    setCustomPopUp({ ...customPopUp, open: false });
    if (code === "VACCINE_CREATE") {
      await getVaccineList("", true);
      methods.setValue("vaccineId", {
        label: data?.vaccineGenericName,
        value: data?.vaccineId,
      });
    } else if (code === "VACCINE_GROUP_CREATE" && rowIndex !== undefined) {
      await getVaccineGroupList("", true);
      methods.setValue(`vaccineDoseColl.${rowIndex}.vaccineGroupId`, {
        label: data?.vaccineGroupName,
        value: data?.vaccineGroupId?.toString(),
      });
    }
    setIsLoading(false);
  };

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    vaccineId: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.number().required(),
      })
      .nullable()
      .required("Vaccine Generic Name is required"),
    vaccineGenericName: Yup.string().nullable(),
    vaccineDoseColl: Yup.array()
      .of(
        Yup.object().shape({
          doseName: Yup.string().required("Dose Name is required"),
          mode: Yup.string().nullable(),
          vaccineGroupId: Yup.object()
            .shape({
              label: Yup.string().required(),
              value: Yup.string().required(),
            })
            .nullable()
            .required("Vaccine group is required"),
          firstDoseRefCode: Yup.string().when("isFirstDose", {
            is: true,
            then: (schema) =>
              schema.required("First Dose Reference is required"),
            otherwise: (schema) => schema.nullable().notRequired(),
          }),
          isDeleted: Yup.boolean().required(),
        }),
      )
      .min(1, "At least one active dose is required.")
      .test(
        "has-active-doses",
        "At least one active dose is required.",
        (value) =>
          value && value.filter((d) => d.isDeleted === false).length > 0,
      )
      .required("Dose Collection is required"),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initVaccineDoseData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  const WatchFields = useWatch({
    control: methods.control,
  });

  const vaccineDoseColl = WatchFields.vaccineDoseColl;
  const vaccineId = WatchFields.vaccineId;

  // GET LIST OF VACCINE
  const getVaccineList = async (searchValue: string, oninit: boolean) => {
    try {
      let vaccineColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", parameters.pageIndex?.toString());
      params.append("pageSize", parameters.pageSize?.toString());
      params.append("sortColumn", parameters.sortColumn);
      params.append("sortDirection", parameters.sortDirection);
      params.append("searchValue", searchValue);
      const result: any = await GetVaccineList(params);
      if (result?.data?.data?.vaccineColl) {
        vaccineColl = result?.data?.data?.vaccineColl;
        if (oninit) {
          setVaccineData(vaccineColl);
        }
        return vaccineColl;
      } else {
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  // FILTER  VACCINE
  async function filterVaccine(inputValue: string) {
    let result = await getVaccineList(inputValue, false);
    return result;
  }

  //  VACCINE LOAD ON SEARCH
  const loadVaccineList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterVaccine(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // SAVE AND UPDATE VACCINE DOSE
  const saveVaccineDose = async (value: formInitialValueTypes) => {
    try {
      let result = value;
      if (result) {
        const payload = {
          vaccineId: result.vaccineId,
          vaccineGenericName: result?.vaccineId?.label,
          standardDurationBetweenPreviousCurrentDoseValue:
            result.standardDurationBetweenPreviousCurrentDoseValue,
          standardDurationBetweenPreviousCurrentDoseMode:
            result.standardDurationBetweenPreviousCurrentDoseMode,
          vaccineDoseColl: result.vaccineDoseColl.map((dose) => ({
            ...dose,
            vaccineGroupId: dose.vaccineGroupId?.value
              ? Number(dose.vaccineGroupId.value)
              : 0,
            vaccineGroupName: dose.vaccineGroupId?.label || "",
            vaccineId: result.vaccineId,
            vaccineGenericName: result?.vaccineId?.label,
            standardDurationBetweenPreviousCurrentDoseValue:
              result.standardDurationBetweenPreviousCurrentDoseValue,
            standardDurationBetweenPreviousCurrentDoseMode:
              result.standardDurationBetweenPreviousCurrentDoseMode,
          })),
        };
        handleClosePopUp("CREATE_DOSE", payload);
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: dose?.vaccineId > 0 ? "Updated" : "Created",
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // HANDLE ADD DETAIL ROW
  const handleAddRow = () => {
    const detailColl = methods.getValues().vaccineDoseColl;
    const standardDurationBetweenPreviousCurrentDoseValue = methods.getValues(
      "standardDurationBetweenPreviousCurrentDoseValue",
    );
    const standardDurationBetweenPreviousCurrentDoseMode = methods.getValues(
      "standardDurationBetweenPreviousCurrentDoseMode",
    );

    const maxRowNumber = detailColl.reduce(
      (acc, curr) => (acc = curr.displayOrder > acc ? curr.displayOrder : acc),
      0,
    );

    const newRow: IFormDetail = {
      ...initVaccineDoseDetail,
      isFirstDose: false,
      durationBetweenPreviousCurrentDoseValue:
        standardDurationBetweenPreviousCurrentDoseValue || 0,
      durationBetweenPreviousCurrentDoseMode:
        standardDurationBetweenPreviousCurrentDoseMode || "",
      displayOrder: maxRowNumber + 1,
    };

    methods.reset({
      ...methods.getValues(),
      vaccineDoseColl: [...detailColl, newRow],
    });
  };

  // HANDLE ROW FIELD CHANGE
  const handleDetailFieldChange = (
    displayOrder: number,
    field: string,
    value: any,
  ) => {
    changeDetailField(displayOrder, field, value);
    methods.trigger(["vaccineDoseColl"]);
  };

  const changeDetailField = (
    displayOrder: number,
    field: string,
    value: any,
  ) => {
    const data = methods.getValues();
    const detailColl = data.vaccineDoseColl;
    const currColl = detailColl[displayOrder];

    if (field?.toLocaleLowerCase() === "isdeleted") {
      currColl.isDeleted = true;
      currColl.mode = "D";
    }
    methods.reset({
      ...data,
    });
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveVaccineDose(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      await getVaccineList("", true);
      await getVaccineGroupList("", true);
      setIsLoading(false);
      if (dose) {
        const mappedDoseColl =
          dose?.doses?.length > 0
            ? dose.doses.map((doseItem: any, index: number) => {
                let parsedDate = null;
                if (
                  doseItem.firstDoseRefDate &&
                  doseItem.firstDoseRefDate instanceof Date
                ) {
                  parsedDate = format(doseItem.firstDoseRefDate, "yyyy-MM-dd");
                } else if (
                  typeof doseItem.firstDoseRefDate === "string" &&
                  doseItem.firstDoseRefDate
                ) {
                  parsedDate = doseItem.firstDoseRefDate;
                }

                return {
                  ...initVaccineDoseDetail,
                  ...doseItem,
                  displayOrder: index,
                  mode: doseItem.vaccineTemplateDoseId > 0 ? "U" : "I",
                  firstDoseAgeValue: Number(doseItem.firstDoseAgeValue) || 0,
                  durationBetweenPreviousCurrentDoseValue:
                    Number(doseItem.durationBetweenPreviousCurrentDoseValue) ||
                    0,
                  maxCatchUpAgeValue: Number(doseItem.maxCatchUpAgeValue) || 0,
                  firstDoseRefDate: parsedDate,
                  vaccineGroupId: doseItem.vaccineGroupId
                    ? {
                        label: doseItem.vaccineGroupName,
                        value: doseItem.vaccineGroupId,
                      }
                    : null,
                };
              })
            : [initVaccineDoseDetail];

        const updatedData: formInitialValueTypes = {
          ...initVaccineDoseData,
          vaccineId: dose.vaccineId
            ? { label: dose.vaccineGenericName, value: dose.vaccineId }
            : null,
          vaccineGenericName: dose.vaccineGenericName || "",
          standardDurationBetweenPreviousCurrentDoseValue:
            dose.standardDurationBetweenPreviousCurrentDoseValue || 0,
          standardDurationBetweenPreviousCurrentDoseMode:
            dose.standardDurationBetweenPreviousCurrentDoseMode || "",
          vaccineDoseColl: mappedDoseColl,
        };

        methods.reset(updatedData);
      } else {
        methods.reset(initVaccineDoseData);
      }
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [vaccineTemplateId, dose, methods]);

  // useEffect(() => {
  //     if (dose) {
  //         const mappedDoseColl = dose?.doses?.length > 0
  //             ? dose.doses.map((doseItem: any, index: number) => {
  //                 let parsedDate = null;
  //                 if (doseItem.firstDoseRefDate && doseItem.firstDoseRefDate instanceof Date) {
  //                     parsedDate = format(doseItem.firstDoseRefDate, "yyyy-MM-dd");
  //                 } else if (typeof doseItem.firstDoseRefDate === 'string' && doseItem.firstDoseRefDate) {
  //                     parsedDate = doseItem.firstDoseRefDate;
  //                 }

  //                 return {
  //                     ...initVaccineDoseDetail,
  //                     ...doseItem,
  //                     displayOrder: index,
  //                     mode: doseItem.vaccineTemplateDoseId > 0 ? "U" : "I",
  //                     firstDoseAgeValue: Number(doseItem.firstDoseAgeValue) || 0,
  //                     durationBetweenPreviousCurrentDoseValue: Number(doseItem.durationBetweenPreviousCurrentDoseValue) || 0,
  //                     maxCatchUpAgeValue: Number(doseItem.maxCatchUpAgeValue) || 0,
  //                     firstDoseRefDate: parsedDate,
  //                 };
  //             })
  //             : [initVaccineDoseDetail];

  //         const updatedData: formInitialValueTypes = {
  //             ...initVaccineDoseData,
  //             vaccineId: dose.vaccineId
  //                 ? { label: dose.vaccineGenericName, value: dose.vaccineId }
  //                 : null,
  //             vaccineGenericName: dose.vaccineGenericName || "",
  //             standardDurationBetweenPreviousCurrentDoseValue: dose.standardDurationBetweenPreviousCurrentDoseValue || 0,
  //             standardDurationBetweenPreviousCurrentDoseMode: dose.standardDurationBetweenPreviousCurrentDoseMode || "",
  //             vaccineDoseColl: mappedDoseColl,
  //         };

  //         methods.reset(updatedData);
  //     } else {
  //         methods.reset(initVaccineDoseData);
  //     }
  // }, [dose, methods]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className={"main-container"}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2} className="group-container">
              <Grid item xs={12}>
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.vaccineId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Vaccine Generic Name *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomAsyncSelect
                          placeholder={"BCG / HEPATITIS B / RABIES"}
                          id="vaccineId"
                          FieldName={"vaccineId"}
                          defaultOptions={vaccineData}
                          loadOptions={loadVaccineList}
                          autoFocus
                          isSearchable
                          isNotCreatable={false}
                          isDisabled={dose?.vaccineId > 0 ? true : false}
                          value={vaccineId}
                          handleChange={(e: any) => {
                            methods.setValue("vaccineId", e || null, {
                              shouldValidate: true,
                            });
                            methods.setValue(
                              "vaccineGenericName",
                              e?.label || "",
                              { shouldValidate: true },
                            );
                          }}
                          onCreateOption={(e: any) => {
                            handleClickOpen(true, e, "VACCINE");
                          }}
                          className={`text-input ${
                            methods.formState.errors.vaccineId ? "error" : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.vaccineId?.message}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={5} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors
                            .standardDurationBetweenPreviousCurrentDoseValue
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Standard Duration Between Dose
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder={"0"}
                          id={"standardDurationBetweenPreviousCurrentDoseValue"}
                          size="small"
                          onFocus={handleFocus}
                          {...methods.register(
                            "standardDurationBetweenPreviousCurrentDoseValue",
                          )}
                          error={
                            !!methods.formState.errors
                              ?.standardDurationBetweenPreviousCurrentDoseValue
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box
                                  className="input-end-andorment-border"
                                  sx={{ minWidth: "110px" }}
                                >
                                  <Controller
                                    control={methods.control}
                                    name={
                                      "standardDurationBetweenPreviousCurrentDoseMode"
                                    }
                                    render={({ field }) => (
                                      <CustomSelect
                                        openMenuOnFocus
                                        id={
                                          "standardDurationBetweenPreviousCurrentDoseMode"
                                        }
                                        FieldName={
                                          "standardDurationBetweenPreviousCurrentDoseMode"
                                        }
                                        menuPortalTarget={document.querySelector(
                                          "body",
                                        )}
                                        options={ageOptions}
                                        isNotCreatable={true}
                                        isIndicatorSeparator={false}
                                        isBorderNone
                                        value={
                                          ageOptions.find(
                                            (opt) => opt.value === field.value,
                                          ) || null
                                        }
                                        handleChange={(e: any) => {
                                          methods.setValue(
                                            "standardDurationBetweenPreviousCurrentDoseMode",
                                            e?.value || "",
                                            { shouldValidate: true },
                                          );
                                        }}
                                      />
                                    )}
                                  />
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: "100px",
                            ".MuiOutlinedInput-root": {
                              paddingRight: 0,
                            },
                          }}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12}>
                  {xld ? (
                    <>
                      <Grid item container>
                        <Grid item xs={12}>
                          <Grid item container gap={2}>
                            {methods
                              .getValues()
                              ?.vaccineDoseColl?.filter(
                                (x) => x.isDeleted === false,
                              )
                              .map(
                                (
                                  row: IFormDetail,
                                  index: number,
                                  arr: Array<IFormDetail>,
                                ) => {
                                  const length = arr.length;
                                  return (
                                    <Grid
                                      item
                                      xs={12}
                                      className={"group-container"}
                                      key={row.displayOrder?.toString()}
                                    >
                                      <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                          <FormControl>
                                            <FormLabel>
                                              First Dose / Follow Up
                                            </FormLabel>
                                            <Controller
                                              name={`vaccineDoseColl.${row.displayOrder}.isFirstDose`}
                                              control={methods.control}
                                              render={({ field }) => {
                                                const isFirstDose =
                                                  field.value ?? false;

                                                return (
                                                  <SplitButton
                                                    options={[
                                                      {
                                                        label: "First Dose",
                                                        value: "first",
                                                      },
                                                      {
                                                        label: "Follow Up",
                                                        value: "follow",
                                                      },
                                                    ]}
                                                    value={
                                                      isFirstDose
                                                        ? "first"
                                                        : "follow"
                                                    }
                                                    onChange={(val) => {
                                                      const newValue =
                                                        val === "first";
                                                      field.onChange(newValue);

                                                      // If First Dose selected, reset duration fields
                                                      if (newValue) {
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseValue`,
                                                          0,
                                                          {
                                                            shouldValidate: false,
                                                          },
                                                        );
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseMode`,
                                                          null,
                                                          {
                                                            shouldValidate: false,
                                                          },
                                                        );
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.firstDoseRefCode`,
                                                          "",
                                                          {
                                                            shouldValidate: false,
                                                          },
                                                        );
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.firstDoseRefName`,
                                                          "",
                                                          {
                                                            shouldValidate: false,
                                                          },
                                                        );
                                                      } else {
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseValue`,
                                                          methods.getValues(
                                                            "standardDurationBetweenPreviousCurrentDoseValue",
                                                          ) || 0,
                                                          {
                                                            shouldValidate: false,
                                                          },
                                                        );
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseMode`,
                                                          methods.getValues(
                                                            "standardDurationBetweenPreviousCurrentDoseMode",
                                                          ) || "",
                                                          {
                                                            shouldValidate: false,
                                                          },
                                                        );
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.firstDoseRefCode`,
                                                          "",
                                                          {
                                                            shouldValidate: false,
                                                          },
                                                        );
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.firstDoseRefName`,
                                                          "",
                                                          {
                                                            shouldValidate: false,
                                                          },
                                                        );
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.firstDoseRefDate`,
                                                          null,
                                                          {
                                                            shouldValidate: false,
                                                          },
                                                        );
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.firstDoseAgeValue`,
                                                          0,
                                                          {
                                                            shouldValidate: false,
                                                          },
                                                        );
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.firstDoseAgeMode`,
                                                          null,
                                                          {
                                                            shouldValidate: false,
                                                          },
                                                        );
                                                      }
                                                    }}
                                                  />
                                                );
                                              }}
                                            />
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                          <FormControl fullWidth>
                                            <FormLabel
                                              className={`fileds-label-text ${
                                                methods.formState.errors
                                                  ?.vaccineDoseColl?.[
                                                  row.displayOrder
                                                ]?.vaccineGroupId
                                                  ? "validation-message"
                                                  : ""
                                              }`}
                                            >
                                              Vaccine age group *
                                            </FormLabel>
                                            <FormControl fullWidth>
                                              <SkeletonWrapper
                                                isLoading={showSkeleton}
                                                className="skeleton-input"
                                              >
                                                <Controller
                                                  control={methods.control}
                                                  name={`vaccineDoseColl.${row.displayOrder}.vaccineGroupId`}
                                                  render={({ field }) => (
                                                    <CustomSelect
                                                      placeholder="Vaccine group"
                                                      isNotCreatable={false}
                                                      isClearable={true}
                                                      id={`vaccineDoseColl.${row.displayOrder}.vaccineGroupId`}
                                                      FieldName={`vaccineDoseColl.${row.displayOrder}.vaccineGroupId`}
                                                      options={
                                                        vaccineGroupOptions
                                                      }
                                                      menuPortalTarget={document.querySelector(
                                                        "body",
                                                      )}
                                                      handleChange={(
                                                        e: any,
                                                      ) => {
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.vaccineGroupId`,
                                                          e,
                                                          {
                                                            shouldValidate: true,
                                                          },
                                                        );
                                                      }}
                                                      onCreateOption={(
                                                        e: any,
                                                      ) => {
                                                        handleClickOpen(
                                                          true,
                                                          e,
                                                          "VACCINE_GROUP",
                                                          row.displayOrder,
                                                        );
                                                      }}
                                                      className={`text-input ${
                                                        methods.formState.errors
                                                          ?.vaccineDoseColl &&
                                                        methods.formState.errors
                                                          ?.vaccineDoseColl[
                                                          row.displayOrder
                                                        ]?.vaccineGroupId
                                                          ? "error"
                                                          : ""
                                                      }`}
                                                      value={field.value}
                                                    />
                                                  )}
                                                />
                                              </SkeletonWrapper>
                                            </FormControl>
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                          <FormControl fullWidth>
                                            <FormLabel>Vaccine Dose</FormLabel>
                                            <SkeletonWrapper
                                              isLoading={showSkeleton}
                                              className="skeleton-input"
                                            >
                                              <CustomTextField
                                                placeholder={
                                                  "Birth Dose / Dose 1 / Booster Dose"
                                                }
                                                autoCapitalize="none"
                                                type="text"
                                                id={`vaccineDoseColl.${row.displayOrder}.doseName`}
                                                size="small"
                                                autoFocus
                                                value={
                                                  row.doseName ||
                                                  dose?.doses
                                                    ?.vaccineGenericName
                                                }
                                                onChange={(e) => {
                                                  methods.setValue(
                                                    `vaccineDoseColl.${row.displayOrder}.doseName`,
                                                    e.target.value,
                                                    {
                                                      shouldValidate: true,
                                                    },
                                                  );
                                                }}
                                                error={
                                                  methods.formState.errors
                                                    ?.vaccineDoseColl &&
                                                  !!methods.formState.errors
                                                    ?.vaccineDoseColl[
                                                    row.displayOrder
                                                  ]?.doseName
                                                }
                                                className={`text-input ${
                                                  methods.formState.errors
                                                    ?.vaccineDoseColl &&
                                                  methods.formState.errors
                                                    ?.vaccineDoseColl[
                                                    row.displayOrder
                                                  ]?.doseName
                                                    ? "error"
                                                    : ""
                                                }`}
                                              />
                                            </SkeletonWrapper>
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                          <FormControl fullWidth>
                                            <FormLabel>
                                              First Dose Ref Name
                                            </FormLabel>
                                            <FormControl fullWidth>
                                              <SkeletonWrapper
                                                isLoading={showSkeleton}
                                                className="skeleton-input"
                                              >
                                                <Controller
                                                  control={methods.control}
                                                  name={`vaccineDoseColl.${row.displayOrder}.firstDoseRefCode`}
                                                  render={({ field }) => (
                                                    <CustomSelect
                                                      isNotCreatable={true}
                                                      isClearable={true}
                                                      id={`vaccineDoseColl.${row.displayOrder}.firstDoseRefCode`}
                                                      FieldName={
                                                        "applyToGender"
                                                      }
                                                      options={
                                                        firstDoseRefOptions
                                                      }
                                                      isDisabled={
                                                        !vaccineDoseColl?.[
                                                          row.displayOrder
                                                        ]?.isFirstDose
                                                      }
                                                      menuPortalTarget={document.querySelector(
                                                        "body",
                                                      )}
                                                      handleChange={(
                                                        e: any,
                                                      ) => {
                                                        const selectedValue =
                                                          e?.value || "";
                                                        const selectedLabel =
                                                          e?.label || "";
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.firstDoseRefCode`,
                                                          selectedValue,
                                                          {
                                                            shouldValidate: true,
                                                          },
                                                        );
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.firstDoseRefName`,
                                                          selectedLabel,
                                                          {
                                                            shouldValidate: true,
                                                          },
                                                        );
                                                        if (
                                                          selectedValue ===
                                                            "TODAY" ||
                                                          selectedValue ===
                                                            "CUSTOM" ||
                                                          selectedValue ===
                                                            "LMP"
                                                        ) {
                                                          const today =
                                                            new Date();
                                                          methods.setValue(
                                                            `vaccineDoseColl.${row.displayOrder}.firstDoseRefDate`,
                                                            today,
                                                            {
                                                              shouldValidate: true,
                                                            },
                                                          );
                                                        }
                                                        if (
                                                          selectedValue ===
                                                          "AGE_BASED"
                                                        ) {
                                                          methods.setValue(
                                                            `vaccineDoseColl.${row.displayOrder}.firstDoseRefDate`,
                                                            null,
                                                            {
                                                              shouldValidate: true,
                                                            },
                                                          );
                                                        }
                                                      }}
                                                      className={`text-input ${
                                                        methods.formState.errors
                                                          ?.vaccineDoseColl &&
                                                        methods.formState.errors
                                                          ?.vaccineDoseColl[
                                                          row.displayOrder
                                                        ]?.firstDoseRefCode
                                                          ? "error"
                                                          : ""
                                                      }`}
                                                      value={
                                                        firstDoseRefOptions.find(
                                                          (opt) =>
                                                            opt.value ===
                                                            field.value,
                                                        ) || null
                                                      }
                                                    />
                                                  )}
                                                />
                                              </SkeletonWrapper>
                                            </FormControl>
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                          <FormControl fullWidth>
                                            <FormLabel>
                                              First Dose Ref
                                            </FormLabel>
                                            <SkeletonWrapper
                                              isLoading={isLoading}
                                              className="skeleton-input"
                                            >
                                              <DatePickerComponent
                                                format="DD-MM-YYYY"
                                                mode="date"
                                                slotProps={{
                                                  textField: { size: "small" },
                                                }}
                                                disabled={
                                                  vaccineDoseColl?.[
                                                    row.displayOrder
                                                  ]?.firstDoseRefCode ===
                                                    "AGE_BASED" ||
                                                  vaccineDoseColl?.[
                                                    row.displayOrder
                                                  ]?.firstDoseRefCode ===
                                                    "TODAY"
                                                }
                                                value={
                                                  methods?.getValues(
                                                    `vaccineDoseColl.${row.displayOrder}.firstDoseRefDate`,
                                                  )
                                                    ? dayjs(
                                                        methods?.getValues(
                                                          `vaccineDoseColl.${row.displayOrder}.firstDoseRefDate`,
                                                        ),
                                                      )
                                                    : null
                                                }
                                                onChange={(e: any) => {
                                                  if (
                                                    e !== null &&
                                                    isValid(e.$d)
                                                  ) {
                                                    const savedDateFormate =
                                                      format(
                                                        e.$d,
                                                        "yyyy-MM-dd",
                                                      );
                                                    methods.setValue(
                                                      `vaccineDoseColl.${row.displayOrder}.firstDoseRefDate`,
                                                      savedDateFormate,
                                                    );
                                                  } else {
                                                    methods.setValue(
                                                      `vaccineDoseColl.${row.displayOrder}.firstDoseRefDate`,
                                                      null,
                                                    );
                                                  }
                                                }}
                                              />
                                            </SkeletonWrapper>
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                          <FormControl fullWidth>
                                            <FormLabel
                                              className={`fileds-label-text  ${
                                                methods.formState.errors
                                                  .billingDate
                                                  ? "validation-message"
                                                  : ""
                                              }`}
                                            >
                                              First Dose Ref Age
                                            </FormLabel>
                                            <FormControl fullWidth>
                                              <SkeletonWrapper
                                                isLoading={showSkeleton}
                                                className="skeleton-input"
                                              >
                                                <CustomTextField
                                                  placeholder={"0"}
                                                  id={`vaccineDoseColl.${row.displayOrder}.firstDoseAgeValue`}
                                                  size="small"
                                                  onFocus={handleFocus}
                                                  disabled={
                                                    !vaccineDoseColl?.[
                                                      row.displayOrder
                                                    ]?.isFirstDose
                                                  }
                                                  {...methods.register(
                                                    `vaccineDoseColl.${row.displayOrder}.firstDoseAgeValue`,
                                                  )}
                                                  error={
                                                    !!methods.formState.errors
                                                      ?.vaccineDoseColl?.[
                                                      row.displayOrder
                                                    ]?.firstDoseAgeValue
                                                  }
                                                  InputProps={{
                                                    endAdornment: (
                                                      <InputAdornment position="end">
                                                        <Box
                                                          className="input-end-andorment-border"
                                                          sx={{
                                                            minWidth: "110px",
                                                          }}
                                                        >
                                                          <Controller
                                                            control={
                                                              methods.control
                                                            }
                                                            name={`vaccineDoseColl.${row.displayOrder}.firstDoseAgeMode`}
                                                            render={({
                                                              field,
                                                            }) => (
                                                              <CustomSelect
                                                                openMenuOnFocus
                                                                id={`vaccineDoseColl.${row.displayOrder}.firstDoseAgeMode`}
                                                                FieldName={`vaccineDoseColl.${row.displayOrder}.firstDoseAgeMode`}
                                                                menuPortalTarget={document.querySelector(
                                                                  "body",
                                                                )}
                                                                options={
                                                                  ageOptions
                                                                }
                                                                isNotCreatable={
                                                                  true
                                                                }
                                                                isIndicatorSeparator={
                                                                  false
                                                                }
                                                                isBorderNone
                                                                isDisabled={
                                                                  !vaccineDoseColl?.[
                                                                    row
                                                                      .displayOrder
                                                                  ]?.isFirstDose
                                                                }
                                                                value={
                                                                  ageOptions.find(
                                                                    (opt) =>
                                                                      opt.value ===
                                                                      field.value,
                                                                  ) || null
                                                                }
                                                                handleChange={(
                                                                  e: any,
                                                                ) => {
                                                                  methods.setValue(
                                                                    `vaccineDoseColl.${row.displayOrder}.firstDoseAgeMode`,
                                                                    e?.value ||
                                                                      "",
                                                                    {
                                                                      shouldValidate: true,
                                                                    },
                                                                  );
                                                                }}
                                                              />
                                                            )}
                                                          />
                                                        </Box>
                                                      </InputAdornment>
                                                    ),
                                                  }}
                                                  sx={{
                                                    minWidth: "150px",
                                                    ".MuiOutlinedInput-root": {
                                                      paddingRight: 0,
                                                    },
                                                  }}
                                                />
                                              </SkeletonWrapper>
                                            </FormControl>
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={11} sm={5} md={5}>
                                          <FormLabel>
                                            Duration Between Prev & Current Dose
                                          </FormLabel>
                                          <FormControl fullWidth>
                                            <SkeletonWrapper
                                              isLoading={showSkeleton}
                                              className="skeleton-input"
                                            >
                                              <CustomTextField
                                                placeholder={"0"}
                                                id={`vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseValue`}
                                                size="small"
                                                disabled={
                                                  vaccineDoseColl?.[
                                                    row.displayOrder
                                                  ]?.isFirstDose
                                                }
                                                onFocus={handleFocus}
                                                {...methods.register(
                                                  `vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseValue`,
                                                )}
                                                error={
                                                  !!methods.formState.errors
                                                    ?.vaccineDoseColl?.[
                                                    row.displayOrder
                                                  ]
                                                    ?.durationBetweenPreviousCurrentDoseValue
                                                }
                                                InputProps={{
                                                  endAdornment: (
                                                    <InputAdornment position="end">
                                                      <Box
                                                        className="input-end-andorment-border"
                                                        sx={{
                                                          minWidth: "110px",
                                                        }}
                                                      >
                                                        <Controller
                                                          control={
                                                            methods.control
                                                          }
                                                          name={`vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseMode`}
                                                          render={({
                                                            field,
                                                          }) => (
                                                            <CustomSelect
                                                              openMenuOnFocus
                                                              id={`vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseMode`}
                                                              FieldName={`vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseMode`}
                                                              menuPortalTarget={document.querySelector(
                                                                "body",
                                                              )}
                                                              options={
                                                                ageOptions
                                                              }
                                                              isNotCreatable={
                                                                true
                                                              }
                                                              isIndicatorSeparator={
                                                                false
                                                              }
                                                              isBorderNone
                                                              isDisabled={
                                                                vaccineDoseColl?.[
                                                                  row
                                                                    .displayOrder
                                                                ]?.isFirstDose
                                                              }
                                                              value={
                                                                ageOptions.find(
                                                                  (opt) =>
                                                                    opt.value ===
                                                                    field.value,
                                                                ) || null
                                                              }
                                                              handleChange={(
                                                                e: any,
                                                              ) => {
                                                                methods.setValue(
                                                                  `vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseMode`,
                                                                  e?.value ||
                                                                    "",
                                                                  {
                                                                    shouldValidate: true,
                                                                  },
                                                                );
                                                              }}
                                                            />
                                                          )}
                                                        />
                                                      </Box>
                                                    </InputAdornment>
                                                  ),
                                                }}
                                                sx={{
                                                  minWidth: "150px",
                                                  ".MuiOutlinedInput-root": {
                                                    paddingRight: 0,
                                                  },
                                                }}
                                              />
                                            </SkeletonWrapper>
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={11} sm={5} md={3}>
                                          <FormLabel>
                                            Max Catch Up Age
                                          </FormLabel>
                                          <FormControl fullWidth>
                                            <SkeletonWrapper
                                              isLoading={showSkeleton}
                                              className="skeleton-input"
                                            >
                                              <CustomTextField
                                                placeholder={"0"}
                                                id={`vaccineDoseColl.${row.displayOrder}.maxCatchUpAgeValue`}
                                                size="small"
                                                onFocus={handleFocus}
                                                value={
                                                  vaccineDoseColl?.[
                                                    row.displayOrder
                                                  ]?.maxCatchUpAgeValue === 0
                                                    ? ""
                                                    : vaccineDoseColl?.[
                                                        row.displayOrder
                                                      ]?.maxCatchUpAgeValue || 0
                                                }
                                                {...methods.register(
                                                  `vaccineDoseColl.${row.displayOrder}.maxCatchUpAgeValue`,
                                                )}
                                                error={
                                                  !!methods.formState.errors
                                                    ?.vaccineDoseColl?.[
                                                    row.displayOrder
                                                  ]?.maxCatchUpAgeValue
                                                }
                                                InputProps={{
                                                  endAdornment: (
                                                    <InputAdornment position="end">
                                                      <Box
                                                        className="input-end-andorment-border"
                                                        sx={{
                                                          minWidth: "110px",
                                                        }}
                                                      >
                                                        <Controller
                                                          control={
                                                            methods.control
                                                          }
                                                          name={`vaccineDoseColl.${row.displayOrder}.maxCatchUpAgeMode`}
                                                          render={({
                                                            field,
                                                          }) => (
                                                            <CustomSelect
                                                              openMenuOnFocus
                                                              id={`vaccineDoseColl.${row.displayOrder}.maxCatchUpAgeMode`}
                                                              FieldName={`vaccineDoseColl.${row.displayOrder}.maxCatchUpAgeMode`}
                                                              menuPortalTarget={document.querySelector(
                                                                "body",
                                                              )}
                                                              options={
                                                                ageOptions
                                                              }
                                                              isNotCreatable={
                                                                true
                                                              }
                                                              isIndicatorSeparator={
                                                                false
                                                              }
                                                              isBorderNone
                                                              value={
                                                                ageOptions.find(
                                                                  (opt) =>
                                                                    opt.value ===
                                                                    field.value,
                                                                ) || null
                                                              }
                                                              handleChange={(
                                                                e: any,
                                                              ) => {
                                                                methods.setValue(
                                                                  `vaccineDoseColl.${row.displayOrder}.maxCatchUpAgeMode`,
                                                                  e?.value ||
                                                                    "",
                                                                  {
                                                                    shouldValidate: true,
                                                                  },
                                                                );
                                                              }}
                                                            />
                                                          )}
                                                        />
                                                      </Box>
                                                    </InputAdornment>
                                                  ),
                                                }}
                                                sx={{
                                                  minWidth: "150px",
                                                  ".MuiOutlinedInput-root": {
                                                    paddingRight: 0,
                                                  },
                                                }}
                                              />
                                            </SkeletonWrapper>
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={1} sm={1} md={1}>
                                          <Box
                                            sx={{
                                              height: "100%",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "end",
                                            }}
                                          >
                                            {length > 1 && (
                                              <ClearIcon
                                                onClick={() => {
                                                  handleDetailFieldChange(
                                                    row.displayOrder,
                                                    "isDeleted",
                                                    true,
                                                  );
                                                }}
                                                style={{
                                                  color: "red",
                                                  cursor: "pointer",
                                                }}
                                              />
                                            )}
                                          </Box>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  );
                                },
                              )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow></TableRow>
                          <TableRow>
                            <TableCell
                              width={"6%"}
                              className="main-dialog-background-color"
                            >
                              First Dose / Follow Up
                            </TableCell>
                            <TableCell
                              width={"17%"}
                              className="main-dialog-background-color"
                            >
                              Vaccine age group *
                            </TableCell>
                            <TableCell
                              width={"17%"}
                              className="main-dialog-background-color"
                            >
                              Vaccine Dose
                            </TableCell>
                            <TableCell
                              width={"17%"}
                              className="main-dialog-background-color"
                            >
                              First Dose Ref Name
                            </TableCell>
                            <TableCell
                              width={"15%"}
                              className="main-dialog-background-color"
                            >
                              First Dose Ref
                            </TableCell>
                            <TableCell
                              width={"15%"}
                              className="main-dialog-background-color"
                            >
                              First Dose Ref Age
                            </TableCell>
                            <TableCell
                              width={"15%"}
                              className="main-dialog-background-color"
                            >
                              Duration Between Prev & Current Dose
                            </TableCell>
                            <TableCell
                              width={"15%"}
                              className="main-dialog-background-color"
                            >
                              Max Catch Up Age
                            </TableCell>
                            <TableCell className="main-dialog-background-color"></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {methods
                            .getValues()
                            ?.vaccineDoseColl?.filter(
                              (x) => x.isDeleted === false,
                            )
                            .map(
                              (
                                row: IFormDetail,
                                index: number,
                                arr: Array<IFormDetail>,
                              ) => {
                                const length = arr.length;
                                return (
                                  <React.Fragment
                                    key={row?.displayOrder?.toString()}
                                  >
                                    <TableRow
                                      key={row.displayOrder?.toString()}
                                    >
                                      <TableCell>
                                        <Controller
                                          name={`vaccineDoseColl.${row.displayOrder}.isFirstDose`}
                                          control={methods.control}
                                          render={({ field }) => {
                                            const isFirstDose =
                                              field.value ?? false;

                                            return (
                                              <SplitButton
                                                options={[
                                                  {
                                                    label: "First Dose",
                                                    value: "first",
                                                  },
                                                  {
                                                    label: "Follow Up",
                                                    value: "follow",
                                                  },
                                                ]}
                                                value={
                                                  isFirstDose
                                                    ? "first"
                                                    : "follow"
                                                }
                                                onChange={(val) => {
                                                  const newValue =
                                                    val === "first";
                                                  field.onChange(newValue);

                                                  // If First Dose selected, reset duration fields
                                                  if (newValue) {
                                                    methods.setValue(
                                                      `vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseValue`,
                                                      0,
                                                      { shouldValidate: false },
                                                    );
                                                    methods.setValue(
                                                      `vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseMode`,
                                                      null,
                                                      { shouldValidate: false },
                                                    );
                                                    methods.setValue(
                                                      `vaccineDoseColl.${row.displayOrder}.firstDoseRefCode`,
                                                      "",
                                                      { shouldValidate: false },
                                                    );
                                                    methods.setValue(
                                                      `vaccineDoseColl.${row.displayOrder}.firstDoseRefName`,
                                                      "",
                                                      { shouldValidate: false },
                                                    );
                                                  } else {
                                                    methods.setValue(
                                                      `vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseValue`,
                                                      methods.getValues(
                                                        "standardDurationBetweenPreviousCurrentDoseValue",
                                                      ) || 0,
                                                      { shouldValidate: false },
                                                    );
                                                    methods.setValue(
                                                      `vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseMode`,
                                                      methods.getValues(
                                                        "standardDurationBetweenPreviousCurrentDoseMode",
                                                      ) || "",
                                                      { shouldValidate: false },
                                                    );
                                                    methods.setValue(
                                                      `vaccineDoseColl.${row.displayOrder}.firstDoseRefCode`,
                                                      "",
                                                      { shouldValidate: false },
                                                    );
                                                    methods.setValue(
                                                      `vaccineDoseColl.${row.displayOrder}.firstDoseRefName`,
                                                      "",
                                                      { shouldValidate: false },
                                                    );
                                                    methods.setValue(
                                                      `vaccineDoseColl.${row.displayOrder}.firstDoseRefDate`,
                                                      null,
                                                      { shouldValidate: false },
                                                    );
                                                    methods.setValue(
                                                      `vaccineDoseColl.${row.displayOrder}.firstDoseAgeValue`,
                                                      0,
                                                      { shouldValidate: false },
                                                    );
                                                    methods.setValue(
                                                      `vaccineDoseColl.${row.displayOrder}.firstDoseAgeMode`,
                                                      null,
                                                      { shouldValidate: false },
                                                    );
                                                  }
                                                }}
                                              />
                                            );
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <FormControl fullWidth>
                                          <SkeletonWrapper
                                            isLoading={showSkeleton}
                                            className="skeleton-input"
                                          >
                                            <Controller
                                              control={methods.control}
                                              name={`vaccineDoseColl.${row.displayOrder}.vaccineGroupId`}
                                              render={({ field }) => (
                                                <CustomSelect
                                                  isNotCreatable={false}
                                                  isClearable={true}
                                                  id={`vaccineDoseColl.${row.displayOrder}.vaccineGroupId`}
                                                  FieldName={`vaccineDoseColl.${row.displayOrder}.vaccineGroupId`}
                                                  options={vaccineGroupOptions}
                                                  menuPortalTarget={document.querySelector(
                                                    "body",
                                                  )}
                                                  handleChange={(e: any) => {
                                                    methods.setValue(
                                                      `vaccineDoseColl.${row.displayOrder}.vaccineGroupId`,
                                                      e,
                                                      {
                                                        shouldValidate: true,
                                                      },
                                                    );
                                                  }}
                                                  onCreateOption={(e: any) => {
                                                    handleClickOpen(
                                                      true,
                                                      e,
                                                      "VACCINE_GROUP",
                                                      row.displayOrder,
                                                    );
                                                  }}
                                                  className={`text-input ${
                                                    methods.formState.errors
                                                      ?.vaccineDoseColl &&
                                                    methods.formState.errors
                                                      ?.vaccineDoseColl[
                                                      row.displayOrder
                                                    ]?.vaccineGroupId
                                                      ? "error"
                                                      : ""
                                                  }`}
                                                  value={field.value}
                                                />
                                              )}
                                            />
                                          </SkeletonWrapper>
                                        </FormControl>
                                      </TableCell>
                                      <TableCell align="right">
                                        <FormControl fullWidth>
                                          <SkeletonWrapper
                                            isLoading={showSkeleton}
                                            className="skeleton-input"
                                          >
                                            <CustomTextField
                                              placeholder={
                                                "Birth Dose / Dose 1 / Booster Dose"
                                              }
                                              autoCapitalize="none"
                                              type="text"
                                              id={`vaccineDoseColl.${row.displayOrder}.doseName`}
                                              size="small"
                                              autoFocus
                                              value={
                                                row.doseName ||
                                                dose?.doses?.vaccineGenericName
                                              }
                                              onChange={(e) => {
                                                methods.setValue(
                                                  `vaccineDoseColl.${row.displayOrder}.doseName`,
                                                  e.target.value,
                                                  {
                                                    shouldValidate: true,
                                                  },
                                                );
                                              }}
                                              error={
                                                methods.formState.errors
                                                  ?.vaccineDoseColl &&
                                                !!methods.formState.errors
                                                  ?.vaccineDoseColl[
                                                  row.displayOrder
                                                ]?.doseName
                                              }
                                              className={`text-input ${
                                                methods.formState.errors
                                                  ?.vaccineDoseColl &&
                                                methods.formState.errors
                                                  ?.vaccineDoseColl[
                                                  row.displayOrder
                                                ]?.doseName
                                                  ? "error"
                                                  : ""
                                              }`}
                                            />
                                          </SkeletonWrapper>
                                        </FormControl>
                                      </TableCell>
                                      {!!vaccineDoseColl?.[row.displayOrder]
                                        ?.isFirstDose ? (
                                        <TableCell>
                                          <FormControl fullWidth>
                                            <SkeletonWrapper
                                              isLoading={showSkeleton}
                                              className="skeleton-input"
                                            >
                                              <Controller
                                                control={methods.control}
                                                name={`vaccineDoseColl.${row.displayOrder}.firstDoseRefCode`}
                                                render={({ field }) => (
                                                  <CustomSelect
                                                    isNotCreatable={true}
                                                    isClearable={true}
                                                    id={`vaccineDoseColl.${row.displayOrder}.firstDoseRefCode`}
                                                    FieldName={"applyToGender"}
                                                    options={
                                                      firstDoseRefOptions
                                                    }
                                                    isDisabled={
                                                      !vaccineDoseColl?.[
                                                        row.displayOrder
                                                      ]?.isFirstDose
                                                    }
                                                    menuPortalTarget={document.querySelector(
                                                      "body",
                                                    )}
                                                    handleChange={(e: any) => {
                                                      const selectedValue =
                                                        e?.value || "";
                                                      const selectedLabel =
                                                        e?.label || "";
                                                      methods.setValue(
                                                        `vaccineDoseColl.${row.displayOrder}.firstDoseRefCode`,
                                                        selectedValue,
                                                        {
                                                          shouldValidate: true,
                                                        },
                                                      );
                                                      methods.setValue(
                                                        `vaccineDoseColl.${row.displayOrder}.firstDoseRefName`,
                                                        selectedLabel,
                                                        {
                                                          shouldValidate: true,
                                                        },
                                                      );
                                                      if (
                                                        selectedValue ===
                                                          "TODAY" ||
                                                        selectedValue ===
                                                          "CUSTOM" ||
                                                        selectedValue === "LMP"
                                                      ) {
                                                        const today =
                                                          new Date();
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.firstDoseRefDate`,
                                                          today,
                                                          {
                                                            shouldValidate: true,
                                                          },
                                                        );
                                                      }
                                                      if (
                                                        selectedValue ===
                                                        "AGE_BASED"
                                                      ) {
                                                        methods.setValue(
                                                          `vaccineDoseColl.${row.displayOrder}.firstDoseRefDate`,
                                                          null,
                                                          {
                                                            shouldValidate: true,
                                                          },
                                                        );
                                                      }
                                                    }}
                                                    className={`text-input ${
                                                      methods.formState.errors
                                                        ?.vaccineDoseColl &&
                                                      methods.formState.errors
                                                        ?.vaccineDoseColl[
                                                        row.displayOrder
                                                      ]?.firstDoseRefCode
                                                        ? "error"
                                                        : ""
                                                    }`}
                                                    value={
                                                      firstDoseRefOptions.find(
                                                        (opt) =>
                                                          opt.value ===
                                                          field.value,
                                                      ) || null
                                                    }
                                                  />
                                                )}
                                              />
                                            </SkeletonWrapper>
                                          </FormControl>
                                        </TableCell>
                                      ) : (
                                        <TableCell></TableCell>
                                      )}
                                      {vaccineDoseColl?.[row.displayOrder]
                                        ?.isFirstDose ? (
                                        vaccineDoseColl?.[row.displayOrder]
                                          ?.firstDoseRefCode !== "DEPENDENT" ? (
                                          <TableCell align="right">
                                            <FormControl fullWidth>
                                              <SkeletonWrapper
                                                isLoading={isLoading}
                                                className="skeleton-input"
                                              >
                                                <DatePickerComponent
                                                  format="DD-MM-YYYY"
                                                  mode="date"
                                                  slotProps={{
                                                    textField: {
                                                      size: "small",
                                                    },
                                                  }}
                                                  disabled={
                                                    vaccineDoseColl?.[
                                                      row.displayOrder
                                                    ]?.firstDoseRefCode ===
                                                      "AGE_BASED" ||
                                                    vaccineDoseColl?.[
                                                      row.displayOrder
                                                    ]?.firstDoseRefCode ===
                                                      "TODAY"
                                                  }
                                                  value={
                                                    methods?.getValues(
                                                      `vaccineDoseColl.${row.displayOrder}.firstDoseRefDate`,
                                                    )
                                                      ? dayjs(
                                                          methods?.getValues(
                                                            `vaccineDoseColl.${row.displayOrder}.firstDoseRefDate`,
                                                          ),
                                                        )
                                                      : null
                                                  }
                                                  onChange={(e: any) => {
                                                    if (
                                                      e !== null &&
                                                      isValid(e.$d)
                                                    ) {
                                                      const savedDateFormate =
                                                        format(
                                                          e.$d,
                                                          "yyyy-MM-dd",
                                                        );
                                                      methods.setValue(
                                                        `vaccineDoseColl.${row.displayOrder}.firstDoseRefDate`,
                                                        savedDateFormate,
                                                      );
                                                    } else {
                                                      methods.setValue(
                                                        `vaccineDoseColl.${row.displayOrder}.firstDoseRefDate`,
                                                        null,
                                                      );
                                                    }
                                                  }}
                                                />
                                              </SkeletonWrapper>
                                            </FormControl>
                                          </TableCell>
                                        ) : (
                                          <TableCell>
                                            <FormControl fullWidth>
                                              <SkeletonWrapper
                                                isLoading={showSkeleton}
                                                className="skeleton-input"
                                              >
                                                <Controller
                                                  control={methods.control}
                                                  name={`vaccineDoseColl.${row.displayOrder}.firstDoseRefVaccineDoseId`}
                                                  render={({ field }) => {
                                                    const filteredOptions = (
                                                      dependentOption || []
                                                    ).filter(
                                                      (opt: any) =>
                                                        opt.vaccineDoseName?.toLowerCase() !==
                                                        row.doseName?.toLowerCase(),
                                                    );

                                                    return (
                                                      <CustomSelect
                                                        isNotCreatable={true}
                                                        isClearable={true}
                                                        id={`vaccineDoseColl.${row.displayOrder}.firstDoseRefVaccineDoseId`}
                                                        FieldName="Dependent On Dose"
                                                        menuPortalTarget={document.querySelector(
                                                          "body",
                                                        )}
                                                        options={
                                                          filteredOptions
                                                        }
                                                        handleChange={(
                                                          e: any,
                                                        ) => {
                                                          field.onChange(
                                                            e ? e.value : null,
                                                          );
                                                        }}
                                                        className={`text-input ${
                                                          methods.formState
                                                            .errors
                                                            ?.vaccineDoseColl?.[
                                                            row.displayOrder
                                                          ]
                                                            ?.firstDoseRefVaccineDoseId
                                                            ? "error"
                                                            : ""
                                                        }`}
                                                        value={
                                                          filteredOptions.find(
                                                            (opt: any) =>
                                                              opt.value ===
                                                              field.value,
                                                          ) || null
                                                        }
                                                      />
                                                    );
                                                  }}
                                                />
                                              </SkeletonWrapper>
                                            </FormControl>
                                          </TableCell>
                                        )
                                      ) : (
                                        <TableCell></TableCell>
                                      )}
                                      {!!vaccineDoseColl?.[row.displayOrder]
                                        ?.isFirstDose ? (
                                        <TableCell>
                                          <FormControl fullWidth>
                                            <SkeletonWrapper
                                              isLoading={showSkeleton}
                                              className="skeleton-input"
                                            >
                                              <CustomTextField
                                                placeholder={"0"}
                                                id={`vaccineDoseColl.${row.displayOrder}.firstDoseAgeValue`}
                                                size="small"
                                                onFocus={handleFocus}
                                                disabled={
                                                  !vaccineDoseColl?.[
                                                    row.displayOrder
                                                  ]?.isFirstDose
                                                }
                                                {...methods.register(
                                                  `vaccineDoseColl.${row.displayOrder}.firstDoseAgeValue`,
                                                )}
                                                error={
                                                  !!methods.formState.errors
                                                    ?.vaccineDoseColl?.[
                                                    row.displayOrder
                                                  ]?.firstDoseAgeValue
                                                }
                                                InputProps={{
                                                  endAdornment: (
                                                    <InputAdornment position="end">
                                                      <Box
                                                        className="input-end-andorment-border"
                                                        sx={{
                                                          minWidth: "110px",
                                                        }}
                                                      >
                                                        <Controller
                                                          control={
                                                            methods.control
                                                          }
                                                          name={`vaccineDoseColl.${row.displayOrder}.firstDoseAgeMode`}
                                                          render={({
                                                            field,
                                                          }) => (
                                                            <CustomSelect
                                                              openMenuOnFocus
                                                              id={`vaccineDoseColl.${row.displayOrder}.firstDoseAgeMode`}
                                                              FieldName={`vaccineDoseColl.${row.displayOrder}.firstDoseAgeMode`}
                                                              menuPortalTarget={document.querySelector(
                                                                "body",
                                                              )}
                                                              options={
                                                                ageOptions
                                                              }
                                                              isNotCreatable={
                                                                true
                                                              }
                                                              isIndicatorSeparator={
                                                                false
                                                              }
                                                              isBorderNone
                                                              isDisabled={
                                                                !vaccineDoseColl?.[
                                                                  row
                                                                    .displayOrder
                                                                ]?.isFirstDose
                                                              }
                                                              value={
                                                                ageOptions.find(
                                                                  (opt) =>
                                                                    opt.value ===
                                                                    field.value,
                                                                ) || null
                                                              }
                                                              handleChange={(
                                                                e: any,
                                                              ) => {
                                                                methods.setValue(
                                                                  `vaccineDoseColl.${row.displayOrder}.firstDoseAgeMode`,
                                                                  e?.value ||
                                                                    "",
                                                                  {
                                                                    shouldValidate: true,
                                                                  },
                                                                );
                                                              }}
                                                            />
                                                          )}
                                                        />
                                                      </Box>
                                                    </InputAdornment>
                                                  ),
                                                }}
                                                sx={{
                                                  minWidth: "150px",
                                                  ".MuiOutlinedInput-root": {
                                                    paddingRight: 0,
                                                  },
                                                }}
                                              />
                                            </SkeletonWrapper>
                                          </FormControl>
                                        </TableCell>
                                      ) : (
                                        <TableCell></TableCell>
                                      )}
                                      {!vaccineDoseColl?.[row.displayOrder]
                                        ?.isFirstDose ? (
                                        <TableCell>
                                          <FormControl fullWidth>
                                            <SkeletonWrapper
                                              isLoading={showSkeleton}
                                              className="skeleton-input"
                                            >
                                              <CustomTextField
                                                placeholder={"0"}
                                                id={`vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseValue`}
                                                size="small"
                                                disabled={
                                                  vaccineDoseColl?.[
                                                    row.displayOrder
                                                  ]?.isFirstDose
                                                }
                                                onFocus={handleFocus}
                                                {...methods.register(
                                                  `vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseValue`,
                                                )}
                                                error={
                                                  !!methods.formState.errors
                                                    ?.vaccineDoseColl?.[
                                                    row.displayOrder
                                                  ]
                                                    ?.durationBetweenPreviousCurrentDoseValue
                                                }
                                                InputProps={{
                                                  endAdornment: (
                                                    <InputAdornment position="end">
                                                      <Box
                                                        className="input-end-andorment-border"
                                                        sx={{
                                                          minWidth: "110px",
                                                        }}
                                                      >
                                                        <Controller
                                                          control={
                                                            methods.control
                                                          }
                                                          name={`vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseMode`}
                                                          render={({
                                                            field,
                                                          }) => (
                                                            <CustomSelect
                                                              openMenuOnFocus
                                                              id={`vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseMode`}
                                                              FieldName={`vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseMode`}
                                                              menuPortalTarget={document.querySelector(
                                                                "body",
                                                              )}
                                                              options={
                                                                ageOptions
                                                              }
                                                              isNotCreatable={
                                                                true
                                                              }
                                                              isIndicatorSeparator={
                                                                false
                                                              }
                                                              isBorderNone
                                                              isDisabled={
                                                                vaccineDoseColl?.[
                                                                  row
                                                                    .displayOrder
                                                                ]?.isFirstDose
                                                              }
                                                              value={
                                                                ageOptions.find(
                                                                  (opt) =>
                                                                    opt.value ===
                                                                    field.value,
                                                                ) || null
                                                              }
                                                              handleChange={(
                                                                e: any,
                                                              ) => {
                                                                methods.setValue(
                                                                  `vaccineDoseColl.${row.displayOrder}.durationBetweenPreviousCurrentDoseMode`,
                                                                  e?.value ||
                                                                    "",
                                                                  {
                                                                    shouldValidate: true,
                                                                  },
                                                                );
                                                              }}
                                                            />
                                                          )}
                                                        />
                                                      </Box>
                                                    </InputAdornment>
                                                  ),
                                                }}
                                                sx={{
                                                  minWidth: "150px",
                                                  ".MuiOutlinedInput-root": {
                                                    paddingRight: 0,
                                                  },
                                                }}
                                              />
                                            </SkeletonWrapper>
                                          </FormControl>
                                        </TableCell>
                                      ) : (
                                        <TableCell></TableCell>
                                      )}
                                      <TableCell>
                                        <FormControl fullWidth>
                                          <SkeletonWrapper
                                            isLoading={showSkeleton}
                                            className="skeleton-input"
                                          >
                                            <CustomTextField
                                              placeholder={"0"}
                                              id={`vaccineDoseColl.${row.displayOrder}.maxCatchUpAgeValue`}
                                              size="small"
                                              onFocus={handleFocus}
                                              value={
                                                vaccineDoseColl?.[
                                                  row.displayOrder
                                                ]?.maxCatchUpAgeValue === 0
                                                  ? ""
                                                  : vaccineDoseColl?.[
                                                      row.displayOrder
                                                    ]?.maxCatchUpAgeValue || 0
                                              }
                                              {...methods.register(
                                                `vaccineDoseColl.${row.displayOrder}.maxCatchUpAgeValue`,
                                              )}
                                              error={
                                                !!methods.formState.errors
                                                  ?.vaccineDoseColl?.[
                                                  row.displayOrder
                                                ]?.maxCatchUpAgeValue
                                              }
                                              InputProps={{
                                                endAdornment: (
                                                  <InputAdornment position="end">
                                                    <Box
                                                      className="input-end-andorment-border"
                                                      sx={{ minWidth: "110px" }}
                                                    >
                                                      <Controller
                                                        control={
                                                          methods.control
                                                        }
                                                        name={`vaccineDoseColl.${row.displayOrder}.maxCatchUpAgeMode`}
                                                        render={({ field }) => (
                                                          <CustomSelect
                                                            openMenuOnFocus
                                                            id={`vaccineDoseColl.${row.displayOrder}.maxCatchUpAgeMode`}
                                                            FieldName={`vaccineDoseColl.${row.displayOrder}.maxCatchUpAgeMode`}
                                                            menuPortalTarget={document.querySelector(
                                                              "body",
                                                            )}
                                                            options={ageOptions}
                                                            isNotCreatable={
                                                              true
                                                            }
                                                            isIndicatorSeparator={
                                                              false
                                                            }
                                                            isBorderNone
                                                            value={
                                                              ageOptions.find(
                                                                (opt) =>
                                                                  opt.value ===
                                                                  field.value,
                                                              ) || null
                                                            }
                                                            handleChange={(
                                                              e: any,
                                                            ) => {
                                                              methods.setValue(
                                                                `vaccineDoseColl.${row.displayOrder}.maxCatchUpAgeMode`,
                                                                e?.value || "",
                                                                {
                                                                  shouldValidate: true,
                                                                },
                                                              );
                                                            }}
                                                          />
                                                        )}
                                                      />
                                                    </Box>
                                                  </InputAdornment>
                                                ),
                                              }}
                                              sx={{
                                                minWidth: "150px",
                                                ".MuiOutlinedInput-root": {
                                                  paddingRight: 0,
                                                },
                                              }}
                                            />
                                          </SkeletonWrapper>
                                        </FormControl>
                                      </TableCell>
                                      <TableCell align="right">
                                        {length > 1 && (
                                          <ClearIcon
                                            onClick={() => {
                                              handleDetailFieldChange(
                                                row.displayOrder,
                                                "isDeleted",
                                                true,
                                              );
                                            }}
                                            style={{
                                              color: "red",
                                              cursor: "pointer",
                                            }}
                                          />
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                );
                              },
                            )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Button
                      color="primary"
                      startIcon={<AddIcon />}
                      variant="contained"
                      type="button"
                      className="primary-button"
                      name="add row"
                      onClick={(e) => {
                        handleAddRow();
                        methods.trigger(["vaccineDoseColl"]);
                      }}
                    >
                      Add Row
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={showSkeleton || isLoading}
                  >
                    {dose?.vaccineId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
      {/* VACCINE */}
      <Dialog
        className="dialogPage"
        open={customPopUp.open && customPopUp.code === "VACCINE"}
        onClose={(e, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        fullScreen
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            width: "1200px",
            height: "700px",
            maxWidth: "none",
          },
        }}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography
              style={{ fontWeight: 600, marginLeft: 0 }}
              sx={{ ml: 2, flex: 1 }}
              variant="h6"
            >
              Save Vaccine
            </Typography>
          </Toolbar>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </AppBar>
        <DialogContent sx={{ padding: "12px 24px" }}>
          <VaccineSave
            handleClosePopUp={(code: any, company: any) => {
              handleManagePopup(code, company);
            }}
            isForPopUp={customPopUp.open}
            name={customPopUp.name}
            code={customPopUp.code}
            item={{
              label: customPopUp.name,
              value: "",
            }}
          />
        </DialogContent>
        <Divider />
      </Dialog>

      {/* VACCINE GROUP */}
      <Dialog
        className="dialogPage"
        open={customPopUp.open && customPopUp.code === "VACCINE_GROUP"}
        onClose={(e, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        fullScreen
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            width: "1200px",
            height: "700px",
            maxWidth: "none",
          },
        }}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography
              style={{ fontWeight: 600, marginLeft: 0 }}
              sx={{ ml: 2, flex: 1 }}
              variant="h6"
            >
              Save Vaccine Group
            </Typography>
          </Toolbar>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </AppBar>
        <DialogContent sx={{ padding: "12px 24px" }}>
          <VaccineGroupSave
            handleClosePopUp={(code: any, company: any) => {
              handleManagePopup(code, company, customPopUp.rowIndex);
            }}
            isForPopUp={customPopUp.open}
            name={customPopUp.name}
            code={customPopUp.code}
            item={{
              label: customPopUp.name,
              value: "",
            }}
          />
        </DialogContent>
        <Divider />
      </Dialog>
    </Box>
  );
}
